/**
 * Converts a color in hex notation to the equivalent rgb notation
 * @param hex - String - ex: "34e9a2"
 * @returns {*}
 */
export const hexToRgb = (hex) => {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

/**
 * Converts a color in hex notation to the equivalent rgba notation
 * @param hex - String - ex: "34e9a2"
 * @returns "rgba(52, 233, 162, 1)"
 */
export const hexToRgba = (hex) => {
    const rgbArray = hexToRgb(hex);
    return `rgba(${rgbArray[0]}, ${rgbArray[1]}, ${rgbArray[2]}, 1)`;
};

/**
 * Converts a color in rgb notation to the equivalent hex notation
 * @param r
 * @param g
 * @param b
 * @returns {string}
 */
export const rgbToHex = (r, g, b) => {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Returns a color (in rgb) that is found on the gradient between two input colors with a given weight.
 * @param color1 - Array representing a color in RGB format - ex: [255,0,0] === #FF0000 === "red"
 * @param color2 - same as color1
 * @param weight - A percentage balance point between the two colors defined as a float between (and including) 0 and 1
 *               - representing the level of gradient we want, ex:
 *               - color1 === "red", color2 === "blue"
 *               - weight === 0 => returned rgb value is "red"
 *               - weight === 1 => returned rgb value is "blue"
 *               - weight === 0.5 => returned rgb value is "128, 0, 128" -> purple (middle color between red and blue)
 * @returns Array
 */
export const pickColor = (color1, color2, weight) => {
    const w = (1 - weight) * 2 - 1;
    const w1 = (w + 1) / 2;
    const w2 = 1 - w1;
    return [
        Math.round(color1[0] * w1 + color2[0] * w2),
        Math.round(color1[1] * w1 + color2[1] * w2),
        Math.round(color1[2] * w1 + color2[2] * w2)
    ];
};

/**
 * Converts a color in hex notation to the equivalent rgba notation with opacity
 * @param hex - String - ex: "#34e9a2"
 * @param diff - Number - ex: "25"
 * @returns "rgba(77, 255, 187, 1)"
 */
export const hexToRgbaWithShift = (hex, diff = 25) => {
    const rgbArray = hexToRgb(hex);

    const newR = rgbArray[0] + diff;
    const newG = rgbArray[1] + diff;
    const newB = rgbArray[2] + diff;

    // Return color with opacity (RGBA)
    return `rgb(${newR > 255 ? 255 : newR}, ${newG > 255 ? 255 : newG}, ${newB > 255 ? 255 : newB}, 1)`;
};
